import React, { useEffect, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import moment from "moment";
import { useUserTimezoneFormatter } from "../../../components/UserTimezoneDateFormatter/UserTimezoneDateFormatter";
import AxiosInstance from "../../AxiosInstance";
import { parseLocalDate } from "../../../utils/dateUtils";
import {
  Dialog,
  DialogTitle,
  Divider,
  Grid,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Stack,
  Tooltip,
  IconButton,
  useTheme,
  useMediaQuery,
  Paper,
  Chip,
} from "@mui/material";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import dayjs from "dayjs";
import Visit from "../../../assets/Blue-sidebar-icon/Appoinment-blue.svg";
import WhiteButton from "../../../components/Button/WhiteButton";
import {
  JobberStatusBadge,
  getVisitAppointmentStatusTone,
} from "../../../components/MuiTable/index.jsx";
import { handleAuth } from "../../../components/Login/Auth";
import { getItem } from "../../../utils/storage";
import { useLocation, useNavigate } from "react-router-dom";
import { LoaderComponent } from "../../../components/Icon/Index";
import { getVisitColors } from "../../../utils/scheduleChipColors.js";
import "./style.css";
import "../../../styles/fullCalendarTodayIndicator.css";

function Schedual() {
  const theme = useTheme();
  const isMobileModal = useMediaQuery(theme.breakpoints.down("sm"));
  const baseUrl = import.meta.env.VITE_BASE_API;
  const navigate = useNavigate();
  const location = useLocation();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState("dayGridMonth");
  const [dateFormat, setDateFormat] = useState("MM/DD/YYYY");
  const [mobileSelectedDate, setMobileSelectedDate] = useState(() => new Date());
  const [mobileDatePickerOpen, setMobileDatePickerOpen] = useState(false);

  const eventsForMobileDate = React.useMemo(() => {
    const d = moment(mobileSelectedDate).startOf("day");
    return events.filter((ev) => ev.start && moment(ev.start).isSame(d, "day"));
  }, [events, mobileSelectedDate]);

  const mobileWeekDays = React.useMemo(() => {
    const start = moment(mobileSelectedDate).startOf("week");
    return Array.from({ length: 7 }, (_, i) => moment(start).add(i, "days").toDate());
  }, [mobileSelectedDate]);

  const openEventModalFromCard = (event) => {
    setSelectedEvent({
      ...event.extendedProps,
      start: event.start,
      end: event.end,
    });
  };

  const getMobileCardTimeLine = (event) => {
    if (event.allDay) return { isAllDay: true, text: "All day" };
    if (event.start && event.end)
      return { isAllDay: false, text: `${moment(event.start).format("h:mm A")} - ${moment(event.end).format("h:mm A")}` };
    if (event.start) return { isAllDay: false, text: moment(event.start).format("h:mm A") };
    return { isAllDay: true, text: "All day" };
  };

  const getMobileCardLines = (event) => {
    const ep = event.extendedProps || {};
    const name = [ep.FirstName, ep.LastName].filter(Boolean).join(" ").trim();
    const lines = [];
    if (name) lines.push(`Client: ${name}`);
    lines.push(`Visit: ${ep.Title || event.title || "Visit"}`);
    return lines;
  };

  // Use timezone-aware date formatter hook
  const formatDateWithUserTimezone = useUserTimezoneFormatter();

  const fetchWorkerScheduleData = async () => {
    try {
      const res = await AxiosInstance.get(
        `/v1/visit/worker-schedule/${getItem("worker_id")}`
      );
      const scheduleData = res.data.data;

      const mappedEvents = scheduleData.map((item) => {
        const firstName = item.customerDetails?.FirstName || "";
        const lastName = item.customerDetails?.LastName || "";
        const customerName = `${firstName} ${lastName}`.trim();
        
        // Create visit object with all necessary properties for getVisitColors
        const visitForColors = {
          Status: item.Status,
          IsComplete: item.IsComplete,
          IsConfirm: item.IsConfirm,
          IsConfirmByWorker: item.IsConfirmByWorker,
          StartDate: item.StartDate, // Pass StartDate for date-based color logic
        };
        
        const visitColors = getVisitColors(visitForColors);

        return {
          id: item.VisitId,
          title: item.ItemName || `Visit (Contract #${item.contractDetails?.ContractNumber})`,
          start: parseLocalDate(item.StartDate),
          end: parseLocalDate(item.EndDate || item.StartDate),
          allDay: true,
          backgroundColor: visitColors.backgroundColor,
          borderColor: visitColors.borderColor,
          textColor: visitColors.textColor,
          classNames: ["visit-event"],
          extendedProps: {
            type: "visit",
            _accent: visitColors.accent,
            _dot: visitColors.dot,
            WorkerId: item.WorkerId,
            VisitId: item.VisitId,
            Title: item.ItemName,
            Note: item.Note,
            VisitContractId: item.ContractId,
            VisitContractNumber: item.contractDetails?.ContractNumber,
            FirstName: firstName,
            LastName: lastName,
            customerName: customerName || "Customer Name not available",
            Address: item.locationDetails?.Address,
            City: item.locationDetails?.City,
            State: item.locationDetails?.State,
            Zip: item.locationDetails?.Zip,
            Country: item.locationDetails?.Country,
            StartTime: item.StartTime,
            EndTime: item.EndTime,
            StartDate: item.StartDate,
            Status: item.Status,
            IsComplete: item.IsComplete,
            IsConfirm: item.IsConfirm,
            IsConfirmByWorker: item.IsConfirmByWorker,
          },
        };
      });

      return mappedEvents;
    } catch (error) {
      console.error("Error:", error.message);
      return [];
    }
  };  

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const workerScheduleEvents = await fetchWorkerScheduleData();
      setEvents(workerScheduleEvents);
      setLoading(false);
    };

    fetchData();
  }, [theme.palette.mode]);

  const renderEventContent = (eventInfo) => {
    const { event } = eventInfo;
    const ext = event?.extendedProps || {};

    const customerName =
      ext.customerName || `${ext.FirstName || ""} ${ext.LastName || ""}`.trim();
    const title = ext.Title || event?.title || "";

    const number = ext.VisitContractNumber;
    const numberLabel = number ? `Visit #${number}` : "Visit";
    const customerLabel = customerName || "Customer";
    const titleLabel = title || "—";

    const tooltipLines = [numberLabel, customerLabel, titleLabel].filter(Boolean);
    const fullTooltipText = tooltipLines.join("\n");

    const accent = ext._accent || event.borderColor || "#94a3b8";
    const dot = ext._dot || accent;

    return (
      <Tooltip
        title={fullTooltipText}
        placement="top"
        arrow
        enterDelay={500}
        leaveDelay={200}
      >
        <div
          className="event-container chip-v2 no-drag"
          style={{ "--chip-accent": accent, "--chip-dot": dot || "transparent" }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <span className="chip-accent-bar" aria-hidden="true" />
          <span className="chip-body">
            <span className="chip-num">{numberLabel}</span>
            <span className="chip-text">
              <span className="chip-customer">{customerLabel}</span>
              <span className="chip-sep"> · </span>
              <span className="chip-title">{titleLabel}</span>
            </span>
          </span>
          {dot && <span className="chip-dot" aria-hidden="true" />}
        </div>
      </Tooltip>
    );
  };

  const eventPropGetter = (event) => {
    const ext = event.extendedProps || {};
    const eventType = ext.type || "";
    const accent = ext._accent || event.borderColor || "#94a3b8";
    const dot = ext._dot || accent;

    const classNames = [];
    if (eventType) {
      classNames.push(`event-type-${eventType}`);
      if (eventType === "visit") classNames.push("visit-event");
    }

    return {
      classNames,
      style: {
        backgroundColor: "transparent",
        borderColor: "transparent",
        color: "#1f2937",
        border: "none",
        borderRadius: "0",
        padding: "0",
        boxShadow: "none",
        "--chip-accent": accent,
        "--chip-dot": dot,
      },
    };
  };

  const handleEventClick = (event) => {
    if (event) {
      setSelectedEvent({
        ...event?.extendedProps,
        start: event?.start,
        end: event?.end,
      });
    }
  };

  const handleClose = () => {
    setSelectedEvent(null);
  };

  if (loading) {
    return (
      <Grid className="Typography-5 m-2 m-md-3" sx={{ width: "100%", maxWidth: "100%" }}>
        <LoaderComponent
          variant="skeleton"
          skeletonLayout="page"
          skeletonMinHeight={"calc(100vh - 220px)"}
          loader={loading}
          height="50"
          width="50"
        />
      </Grid>
    );
  }

  return (
    <Grid
      style={{
        width: "100%",
        maxWidth: "1500px",
        margin: "0 auto",
        borderRadius: "8px",
        boxShadow: "var(--shadow-md)",
      }}
    >
      {selectedEvent && (
        <Dialog
          open={!!selectedEvent}
          onClose={handleClose}
          fullWidth
          maxWidth="sm"
          className="schedule-event-detail-dialog"
          scroll="paper"
          PaperProps={{
            sx: {
              borderRadius: "12px",
              overflow: "hidden",
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              boxShadow: "var(--shadow-lg)",
              display: "flex",
              flexDirection: "column",
              maxHeight: isMobileModal ? "90vh" : "min(92vh, 720px)",
              m: isMobileModal ? "12px" : undefined,
              maxWidth: { xs: "calc(100% - 24px)", sm: "520px" },
            },
          }}
        >
          {(() => {
            const customerName = [selectedEvent?.FirstName, selectedEvent?.LastName]
              .filter(Boolean)
              .join(" ")
              .trim() || selectedEvent?.customerName || "";
            const numberLabel = selectedEvent?.VisitContractNumber
              ? `Visit · Contract #${selectedEvent?.VisitContractNumber}`
              : "Visit";
            const subtitleText = [numberLabel, customerName].filter(Boolean).join(" · ");
            return (
              <DialogTitle
                component="div"
                className="schedule-event-dialog-title"
                sx={{
                  m: 0,
                  px: { xs: 2.5, sm: 3 },
                  pt: { xs: 2, sm: 2.5 },
                  pb: 1.75,
                  background: "#ffffff",
                  borderBottom: "1px solid #e5e7eb",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1.5,
                }}
              >
                <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
                  <Typography
                    component="div"
                    className="schedule-event-dialog-title-text"
                    sx={{
                      fontSize: { xs: "1.0625rem", sm: "1.125rem" },
                      fontWeight: 600,
                      color: "#0f172a",
                      lineHeight: 1.3,
                      letterSpacing: "-0.01em",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Visit Details
                  </Typography>
                  {subtitleText && (
                    <Typography
                      component="div"
                      sx={{
                        mt: 0.25,
                        fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                        fontWeight: 400,
                        color: "#64748b",
                        lineHeight: 1.4,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {subtitleText}
                    </Typography>
                  )}
                </Box>
                <IconButton
                  type="button"
                  onClick={handleClose}
                  aria-label="Close dialog"
                  size="small"
                  sx={{
                    flexShrink: 0,
                    mt: -0.25,
                    color: "#64748b",
                    borderRadius: "8px",
                    "&:hover": { bgcolor: "#f1f5f9", color: "#0f172a" },
                  }}
                >
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              </DialogTitle>
            );
          })()}
          <DialogContent
            className="schedule-event-detail-dialog-content"
            sx={{
              bgcolor: "background.default",
              color: "text.primary",
              px: { xs: "14px", sm: "20px" },
              pb: { xs: "12px", sm: "16px" },
              pt: { xs: "18px !important", sm: "24px !important" },
              flex: "1 1 auto",
              minHeight: 0,
              overflowY: "auto",
            }}
          >
            <Box className="schedule-event-detail-card" sx={{ mb: 2 }}>
              <Grid container spacing={0} className="schedule-event-detail-fields">
                  <Grid size={12}>
                    <div
                      style={{ color: "var(--text-secondary)", fontWeight: 600, fontSize: 15 }}
                    >
                      Contract Number
                    </div>
                    <div style={{ color: "var(--text-primary)", fontSize: 15 }}>
                      Visit ( Contract #{selectedEvent?.VisitContractNumber} )
                    </div>
                  </Grid>
                  <Grid size={12}>
                    <div
                      style={{ color: "var(--text-secondary)", fontWeight: 600, fontSize: 15 }}
                    >
                      Visit Title
                    </div>
                    <div style={{ color: "var(--text-primary)", fontSize: 15 }}>
                      {selectedEvent?.Title || "N/A"}
                    </div>
                  </Grid>
                  <Grid size={12}>
                    <div
                      style={{ color: "var(--text-secondary)", fontWeight: 600, fontSize: 15 }}
                    >
                      Customer Name
                    </div>
                    <div style={{ color: "var(--text-primary)", fontSize: 15 }}>
                      {selectedEvent?.customerName ||
                      (selectedEvent?.FirstName && selectedEvent?.LastName
                        ? `${selectedEvent.FirstName} ${selectedEvent.LastName}`
                        : selectedEvent?.FirstName
                        ? selectedEvent.FirstName
                        : "N/A")}
                    </div>
                  </Grid>
                  {selectedEvent?.Status && (
                    <Grid size={12}>
                      <div
                        style={{ color: "var(--text-secondary)", fontWeight: 600, fontSize: 15 }}
                      >
                        Status
                      </div>
                      <div className="schedule-event-status-value">
                        <JobberStatusBadge
                          tone={getVisitAppointmentStatusTone(
                            selectedEvent.Status
                          )}
                        >
                          {selectedEvent.Status}
                        </JobberStatusBadge>
                      </div>
                    </Grid>
                  )}
                  {selectedEvent?.start && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <div
                        style={{ color: "var(--text-secondary)", fontWeight: 600, fontSize: 15 }}
                      >
                        Start Date
                      </div>
                      <div style={{ color: "var(--text-primary)", fontSize: 15 }}>
                        {formatDateWithUserTimezone(selectedEvent.start, {
                          dateFormat: dateFormat || "MM/DD/YYYY",
                          includeSeconds: false,
                          excludeTime: true,
                        })}
                      </div>
                    </Grid>
                  )}
                  {selectedEvent?.end && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <div
                        style={{ color: "var(--text-secondary)", fontWeight: 600, fontSize: 15 }}
                      >
                        End Date
                      </div>
                      <div style={{ color: "var(--text-primary)", fontSize: 15 }}>
                        {formatDateWithUserTimezone(selectedEvent.end, {
                          dateFormat: dateFormat || "MM/DD/YYYY",
                          includeSeconds: false,
                          excludeTime: true,
                        })}
                      </div>
                    </Grid>
                  )}
                  {selectedEvent?.StartTime && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <div
                        style={{ color: "var(--text-secondary)", fontWeight: 600, fontSize: 15 }}
                      >
                        Start Time
                      </div>
                      <div style={{ color: "var(--text-primary)", fontSize: 15 }}>
                        {formatDateWithUserTimezone(selectedEvent.StartTime, {
                          dateFormat: "hh:mm A",
                          timeFormat: "12h",
                          includeSeconds: false,
                        })}
                      </div>
                    </Grid>
                  )}
                  {selectedEvent?.EndTime && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <div
                        style={{ color: "var(--text-secondary)", fontWeight: 600, fontSize: 15 }}
                      >
                        End Time
                      </div>
                      <div style={{ color: "var(--text-primary)", fontSize: 15 }}>
                        {formatDateWithUserTimezone(selectedEvent.EndTime, {
                          dateFormat: "hh:mm A",
                          timeFormat: "12h",
                          includeSeconds: false,
                        })}
                      </div>
                    </Grid>
                  )}
                  {selectedEvent?.Note && (
                    <Grid size={12}>
                      <div
                        style={{ color: "var(--text-secondary)", fontWeight: 600, fontSize: 15 }}
                      >
                        Note
                      </div>
                      <div style={{ color: "var(--text-primary)", fontSize: 15 }}>
                        {selectedEvent.Note}
                      </div>
                    </Grid>
                  )}
                  <Grid className="schedule-event-visit-status-row" size={12}>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 1.25,
                        rowGap: 1,
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: "var(--text-secondary)",
                          width: { xs: "100%", sm: "auto" },
                          mr: { sm: 0.5 },
                        }}
                      >
                        Visit status
                      </Typography>
                      <Chip
                        size="small"
                        icon={
                          selectedEvent?.IsConfirm ||
                          selectedEvent?.IsConfirmByWorker ? (
                            <CheckCircleOutlineIcon
                              sx={{ fontSize: "18px !important" }}
                            />
                          ) : undefined
                        }
                        label={
                          selectedEvent?.IsConfirm ||
                          selectedEvent?.IsConfirmByWorker
                            ? "Confirmed"
                            : "Not confirmed"
                        }
                        color={
                          selectedEvent?.IsConfirm ||
                          selectedEvent?.IsConfirmByWorker
                            ? "success"
                            : "default"
                        }
                        variant={
                          selectedEvent?.IsConfirm ||
                          selectedEvent?.IsConfirmByWorker
                            ? "filled"
                            : "outlined"
                        }
                        sx={{
                          fontWeight: 600,
                          borderColor: "var(--border)",
                          ...(selectedEvent?.IsConfirm ||
                          selectedEvent?.IsConfirmByWorker
                            ? {}
                            : { bgcolor: "var(--bg-main)", color: "var(--text-secondary)" }),
                        }}
                      />
                      <Chip
                        size="small"
                        icon={
                          selectedEvent?.IsComplete ? (
                            <EventAvailableOutlinedIcon
                              sx={{ fontSize: "18px !important" }}
                            />
                          ) : undefined
                        }
                        label={
                          selectedEvent?.IsComplete
                            ? "Completed"
                            : "Not completed"
                        }
                        variant={
                          selectedEvent?.IsComplete ? "filled" : "outlined"
                        }
                        sx={{
                          fontWeight: 600,
                          borderColor: "var(--border)",
                          ...(selectedEvent?.IsComplete
                            ? {
                                bgcolor: "var(--navy-deep)",
                                color: "#fff",
                                border: "none",
                                "& .MuiChip-icon": {
                                  color: "#fff !important",
                                },
                              }
                            : { bgcolor: "var(--bg-main)", color: "var(--text-secondary)" }),
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
            </Box>
            <Box className="schedule-event-location-block" sx={{ mt: 0.5, mb: 0 }}>
              <Typography
                className="schedule-event-location-label"
                sx={{
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "text.secondary",
                  mb: 1,
                  px: 0.25,
                  borderLeft: "4px solid #e88c44",
                  pl: 1.5,
                }}
              >
                Location
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  border: "1px solid var(--border)",
                  borderRadius: "10px",
                  bgcolor: "action.hover",
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 1.25, sm: 1.5 },
                  boxShadow: "var(--shadow-sm)",
                }}
              >
                <Typography
                  component="div"
                  sx={{
                    color: "text.primary",
                    fontSize: "0.9375rem",
                    fontWeight: 500,
                    lineHeight: 1.5,
                  }}
                >
                  {selectedEvent
                    ? `${selectedEvent.Address ? `${selectedEvent.Address}, ` : ""}${selectedEvent.City || "-"}, ${selectedEvent.State || "-"} ${selectedEvent.Zip || "-"}`
                    : " - "}
                </Typography>
              </Paper>
            </Box>
          </DialogContent>
          <Divider
            sx={{ borderColor: "var(--border)" }}
            className="schedule-event-dialog-divider"
          />
          <DialogActions
            className="schedule-event-dialog-actions"
            sx={{
              flexShrink: 0,
              justifyContent: "flex-start",
              padding: { xs: "12px 16px", sm: "12px 20px" },
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: "12px", sm: "0" },
              bgcolor: "var(--bg-main)",
              borderTop: "1px solid var(--border)",
              "& button": { width: { xs: "100%", sm: "auto" } },
            }}
          >
            <Box
              sx={{
                width: { xs: "100%", sm: "auto" },
                "& button": { width: { xs: "100%", sm: "auto" } },
              }}
            >
              <WhiteButton
                onClick={handleClose}
                label="Close"
                style={{
                  borderRadius: "5px",
                  padding: "8px 16px",
                  fontWeight: "600",
                  textTransform: "uppercase",
                }}
              />
            </Box>
          </DialogActions>
        </Dialog>
      )}
      <div className="calendar-wrapper desktop-view">
        <Grid style={{ position: "relative" }}>
          <Box sx={{ 
            position: "relative", 
            maxWidth: "100%", 
            width: "100%",
            overflowX: "hidden",
            overflowY: "auto"
          }}>
            <Box
              className="calender-main"
              sx={{
                position: "relative",
                minWidth: "100%",
                maxWidth: "100%",
                width: "100%",
                height: "100%",
                overflow: "hidden",
                border: "1px solid var(--border)",
                borderRadius: "8px",
                padding: "10px",
              }}
            >
              <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, listPlugin]}
              initialView={currentView}
              events={events}
              eventContent={renderEventContent}
              eventClick={(info) => handleEventClick(info.event)}
              headerToolbar={{
                left: "prev,today,next",
                center: "title",
                right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
              }}
              eventPropGetter={eventPropGetter}
              height="auto"
              contentHeight="auto"
              slotMinTime="07:00:00"
              slotMaxTime="20:00:00"
              allDaySlot={true}
              slotDuration="01:00:00"
              slotLabelInterval="01:00:00"
              weekends={true}
              dayHeaderFormat={{ weekday: "short" }}
              /* dayHeaderContent removed — moment(arg.date).format('ddd') was
                 off-by-one because FullCalendar passes arg.date as
                 midnight-UTC and moment converts to local time.
                 dayHeaderFormat above already produces the correct
                 abbreviation. */
              views={{
                timeGridWeek: {
                  slotLabelFormat: {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  },
                  allDaySlot: true,
                  dayHeaderFormat: { weekday: "short" },
                },
                timeGridDay: {
                  slotLabelFormat: {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  },
                  allDaySlot: true,
                  dayHeaderFormat: { weekday: "short" },
                },
                dayGridMonth: {
                  allDaySlot: true,
                  dayHeaderFormat: { weekday: "short" },
                },
              }}
              eventDisplay="block"
              eventStartEditable={false}
              eventDurationEditable={false}
            />
          </Box>
        </Box>
      </Grid>
      </div>
      <div className="calendar-wrapper mobile-view">
        <div className="mobile-schedule-inner">
          <header className="mobile-calendar-header">
            <button
              type="button"
              className="mobile-date-picker-trigger"
              onClick={() => setMobileDatePickerOpen(true)}
              aria-label="Select date"
            >
              <span className="mobile-date-one-line">
                {moment(mobileSelectedDate).format("dddd MMMM D, YYYY")}
              </span>
              <CalendarMonthRoundedIcon className="mobile-date-picker-icon" />
            </button>
            <div className="mobile-day-strip">
              {mobileWeekDays.map((day) => {
                const isSelected = moment(day).isSame(moment(mobileSelectedDate), "day");
                const isToday = moment(day).isSame(moment(), "day");
                return (
                  <button
                    key={day.getTime()}
                    type="button"
                    className={`mobile-day-dot ${isSelected ? "selected" : ""}${isToday ? " is-today" : ""}`}
                    onClick={() => setMobileSelectedDate(day)}
                    aria-label={moment(day).format("ddd MMM D")}
                  >
                    {moment(day).format("ddd").charAt(0)}
                  </button>
                );
              })}
            </div>
          </header>
          <div className="mobile-event-list">
            {eventsForMobileDate.length === 0 ? (
              <p className="mobile-no-events">No events on this date.</p>
            ) : (
              eventsForMobileDate.map((event) => {
                const timeLine = getMobileCardTimeLine(event);
                const lines = getMobileCardLines(event);
                return (
                  <div
                    key={event.id}
                    className="event-card-mobile type-visit"
                    onClick={() => openEventModalFromCard(event)}
                  >
                    <div className="event-card-mobile-time">
                      {timeLine.isAllDay ? (
                        <span className="event-time-all-day">{timeLine.text}</span>
                      ) : (
                        <span className="event-time-range">{timeLine.text}</span>
                      )}
                    </div>
                    {lines.length > 0 && (
                      <div className="event-card-mobile-lines">
                        {lines.map((line, idx) => (
                          <div key={idx} className="event-card-mobile-line">{line}</div>
                        ))}
                      </div>
                    )}
                    <button
                      type="button"
                      className="view-details-btn"
                      onClick={(e) => { e.stopPropagation(); openEventModalFromCard(event); }}
                    >
                      View Info
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
      <Dialog
        open={mobileDatePickerOpen}
        onClose={() => setMobileDatePickerOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: 16, padding: 8 } }}
        className="mobile-date-picker-dialog"
      >
        <DialogTitle style={{ textAlign: "center", paddingBottom: 0 }}>Select date</DialogTitle>
        <DialogContent style={{ paddingTop: 8 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateCalendar
              value={dayjs(mobileSelectedDate)}
              onChange={(newDate) => {
                if (newDate) {
                  setMobileSelectedDate(newDate.toDate());
                  setMobileDatePickerOpen(false);
                }
              }}
              showDaysOutsideCurrentMonth
            />
          </LocalizationProvider>
        </DialogContent>
      </Dialog>
    </Grid>
  );
}

export default Schedual;
